import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  EventqDialogPopover,
  EventqDialogPopoverPlacement,
} from '../dialog-popover/dialog-popover';
import { EventqEventOutcomeList } from '../event-outcome-list/event-outcome-list';
import type {
  EventOutcomeConfirm,
  EventOutcomeGroup,
  EventOutcomePriority,
} from '../event-outcome/event-outcome.types';
import { EVENT_OUTCOME_PRIORITY_ICONS } from '../event-outcome/event-outcome.types';
import { EventqInput } from '../../primitives/input/input';
import { EventqPopoverSearch } from '../popover-search/popover-search';
import {
  EventqPriorityFilterOption,
  EventqPriorityFilterRow,
} from '../priority-filter-row/priority-filter-row';

export type { EventOutcomeConfirm, EventOutcomeGroup, EventOutcomePriority };

const CLOSING_DETAILS_MAX_LENGTH = 200;

const DEFAULT_CLOSE_PRIORITY_OPTIONS: EventqPriorityFilterOption[] = [
  { id: 'all', label: 'ALL' },
  { id: 'p4', label: 'P4', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p4 },
  { id: 'p5', label: 'P5', iconSrc: EVENT_OUTCOME_PRIORITY_ICONS.p5 },
];

@Component({
  selector: 'eventq-close-event-popover',
  imports: [
    EventqDialogPopover,
    EventqPopoverSearch,
    EventqPriorityFilterRow,
    EventqEventOutcomeList,
    EventqInput,
  ],
  templateUrl: './close-event-popover.html',
  styleUrl: './close-event-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqCloseEventPopover {
  readonly open = input(false);
  readonly groups = input.required<EventOutcomeGroup[]>();
  readonly searchQuery = input('');
  readonly selectedPriorities = input<EventOutcomePriority[]>([]);
  readonly selectedOutcomeId = input<string | null>(null);
  readonly closingDetails = input('');
  readonly submitAttempted = input(false);
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement | 'auto'>('auto');
  readonly panelWidth = input<string | null>(null);
  readonly priorityOptions = input(DEFAULT_CLOSE_PRIORITY_OPTIONS);
  readonly cancelLabel = input('Cancel');
  readonly confirmLabel = input('Submit and Close Event');
  readonly closingDetailsMaxLength = CLOSING_DETAILS_MAX_LENGTH;

  readonly closed = output<void>();
  readonly confirm = output<EventOutcomeConfirm>();
  readonly searchQueryChange = output<string>();
  readonly selectedPrioritiesChange = output<EventOutcomePriority[]>();
  readonly selectedOutcomeIdChange = output<string>();
  readonly closingDetailsChange = output<string>();

  readonly resolvedPlacement = computed((): EventqDialogPopoverPlacement => {
    const placement = this.placement();
    if (placement !== 'auto') {
      return placement;
    }

    return this.anchor() ? 'anchor-top-end' : 'center';
  });

  readonly confirmDisabled = computed(
    () =>
      !this.selectedOutcomeId() ||
      this.closingDetails().trim().length === 0 ||
      this.closingDetails().length > this.closingDetailsMaxLength,
  );

  onConfirm(): void {
    const outcomeId = this.selectedOutcomeId();
    const rawClosingDetails = this.closingDetails();
    const closingDetails = rawClosingDetails.trim();

    if (!outcomeId || !closingDetails || rawClosingDetails.length > this.closingDetailsMaxLength) {
      return;
    }

    this.confirm.emit({ outcomeId, closingDetails });
  }
}
